import { useEffect, useRef, useState } from "react";

/**
 * Reusable textarea with `@`-typeahead mention support.
 *
 * Props:
 *   value, onChange     — controlled textarea value.
 *   team                — array of {id, name, role} to search against.
 *   onMentionsChange    — called with the deduped list of selected users
 *                         (mirrors ContractDetail's pendingMentions pattern).
 *   resetSignal         — when this value changes, internal picked chips reset
 *                         (call after a successful submit).
 *   placeholder, rows, testIdPrefix, className, minHeight — passthroughs.
 */
export default function MentionInput({
  value,
  onChange,
  team = [],
  onMentionsChange,
  resetSignal,
  placeholder = "",
  rows = 3,
  testIdPrefix = "mention",
  className = "",
  minHeight,
}) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState([]); // {id, name, role}

  useEffect(() => { setPicked([]); setOpen(false); }, [resetSignal]);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    const cursor = e.target.selectionStart ?? v.length;
    const before = v.slice(0, cursor);
    const m = before.match(/@([\w-]{0,30})$/);
    if (m) {
      setQuery(m[1].toLowerCase());
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const insert = (u) => {
    const v = value || "";
    const cursor = ref.current?.selectionStart ?? v.length;
    const before = v.slice(0, cursor);
    const after = v.slice(cursor);
    const rep = before.replace(/@[\w-]{0,30}$/, `@${u.name} `);
    onChange(rep + after);
    setPicked(prev => {
      const next = prev.find(x => x.id === u.id) ? prev : [...prev, u];
      onMentionsChange && onMentionsChange(next);
      return next;
    });
    setOpen(false);
    setTimeout(() => ref.current?.focus(), 0);
  };

  const clearMention = (id) => {
    setPicked(prev => {
      const next = prev.filter(x => x.id !== id);
      onMentionsChange && onMentionsChange(next);
      return next;
    });
  };

  const results = query
    ? team.filter(u => u.name.toLowerCase().includes(query)).slice(0, 6)
    : team.slice(0, 6);

  return (
    <div className="relative">
      <textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        rows={rows}
        placeholder={placeholder}
        className={`w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:border-[#005289] focus:outline-none resize-none ${className}`}
        style={minHeight ? { minHeight } : undefined}
        data-testid={`${testIdPrefix}-textarea`}
      />
      {open && results.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded shadow-lg z-50 max-h-60 overflow-y-auto"
             data-testid={`${testIdPrefix}-dropdown`}>
          {results.map(u => (
            <button key={u.id} onClick={() => insert(u)} type="button"
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 text-xs border-b border-slate-100 last:border-b-0"
                    data-testid={`${testIdPrefix}-option-${u.id}`}>
              <div className="font-semibold text-slate-800">{u.name}</div>
              <div className="text-[10px] text-slate-400">{(u.role || "").replace(/_/g, " ")}</div>
            </button>
          ))}
        </div>
      )}
      {picked.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`${testIdPrefix}-chips`}>
          {picked.map(m => (
            <span key={m.id} className="inline-flex items-center gap-1 text-[10px] bg-[#005289] text-white rounded-full px-2 py-0.5 font-semibold"
                  data-testid={`${testIdPrefix}-chip-${m.id}`}>
              @{m.name}
              <button onClick={() => clearMention(m.id)} className="hover:text-red-200" type="button"
                      data-testid={`${testIdPrefix}-chip-remove-${m.id}`}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
